/**
 * FeatureGate — conditionally renders children based on feature availability.
 *
 * Usage:
 *   <FeatureGate feature="ai_copilot">
 *     <AICopilotPanel />
 *   </FeatureGate>
 *
 *   <FeatureGate feature="ai_copilot" showUpgrade featureLabel="AI Copilot">
 *     <AICopilotPanel />
 *   </FeatureGate>
 */

import React, { useContext } from 'react';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useUpgradeModal, UpgradeModalContext } from '../../context/UpgradeModalContext';

/**
 * FeatureUpgradeCTA — inline upgrade prompt shown when a feature is unavailable.
 */
function FeatureUpgradeCTA({ feature, featureLabel }) {
  const { openUpgradeModal } = useUpgradeModal();
  const label = featureLabel || feature;

  return (
    <div className="rounded-xl border border-indigo-800/60 bg-indigo-950/30 p-6 text-center">
      <div className="text-indigo-400 text-3xl mb-3">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
      </div>
      <h3 className="text-white font-semibold text-lg">{label}</h3>
      <p className="text-gray-400 text-sm mt-2 max-w-sm mx-auto">
        This feature is available on a higher plan. Upgrade to unlock it.
      </p>
      <button
        onClick={() => openUpgradeModal({
          headline: `Unlock ${label}`,
          subtext: `Upgrade your plan to access ${label} and more.`,
        })}
        className="mt-4 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg font-medium transition-colors"
      >
        See plans
      </button>
    </div>
  );
}

export default function FeatureGate({ feature, children, fallback = null, showUpgrade = false, featureLabel }) {
  const { canAccess, isLoading } = useEntitlements();

  // While loading, don't flash content — render nothing
  if (isLoading) return null;

  if (!canAccess(feature)) {
    if (showUpgrade) {
      return <FeatureUpgradeCTA feature={feature} featureLabel={featureLabel} />;
    }
    return fallback;
  }

  return <>{children}</>;
}

/**
 * UsageLimitBanner — inline banner shown when a resource is at/near its plan cap.
 *
 * Usage:
 *   <UsageLimitBanner resource="campaigns" label="campaigns" />
 */
export function UsageLimitBanner({ resource, label }) {
  const { getUsage, isLoading } = useEntitlements();
  // Use context directly to avoid throwing when provider is absent (e.g. in tests)
  const modal = useContext(UpgradeModalContext);

  if (isLoading) return null;

  const { used, limit } = getUsage(resource);
  if (limit === -1 || limit === 0) return null;

  const pct = (used / limit) * 100;
  if (pct < 80) return null;

  const atLimit = used >= limit;

  const handleUpgrade = () => {
    if (modal) {
      modal.openUpgradeModal({
        headline: `Need more ${label}?`,
        subtext: `You've used ${used} of ${limit} ${label}. Upgrade for higher limits.`,
      });
    } else {
      window.location.href = '/settings/billing';
    }
  };

  return (
    <div className={`rounded-lg border px-4 py-2.5 flex items-center justify-between mb-4 ${
      atLimit ? 'border-red-700/60 bg-red-950/30' : 'border-amber-700/60 bg-amber-950/30'
    }`}>
      <p className={`text-sm ${atLimit ? 'text-red-300' : 'text-amber-300'}`}>
        {atLimit
          ? `${label} limit reached (${used}/${limit}). Upgrade to add more.`
          : `${used}/${limit} ${label} used. You're approaching your limit.`}
      </p>
      <button
        onClick={handleUpgrade}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors whitespace-nowrap ml-3 shrink-0"
      >
        Upgrade
      </button>
    </div>
  );
}

/**
 * BetaBadge — small label shown next to beta features.
 */
export function BetaBadge() {
  return (
    <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">
      Beta
    </span>
  );
}
